import { Controller } from "@hotwired/stimulus";

// Handles static map placeholder and upgrade to interactive
// Shows cheap static image ($2/1000) until user interacts, then upgrades to dynamic ($7/1000)
export default class MapStaticController extends Controller {
  static targets = ["embed"];
  static outlets = ["map"]; // For triggering interactive map initialization
  static values = {
    staticUrl: String,
    mode: { type: String, default: "static" }, // "static" or "interactive"
  };

  static PREFERENCE_KEY = "faithstreet:map:interactive";

  hasInteractivePreference() {
    try {
      return (
        localStorage.getItem(MapStaticController.PREFERENCE_KEY) === "true"
      );
    } catch {
      return false; // localStorage unavailable (private browsing, etc.)
    }
  }

  saveInteractivePreference() {
    try {
      localStorage.setItem(MapStaticController.PREFERENCE_KEY, "true");
    } catch {
      // Ignore - localStorage unavailable
    }
  }

  // Called by map_controller to check if should skip static mode
  shouldSkipStatic() {
    return this.hasInteractivePreference();
  }

  // Called by map_controller to render static map
  renderStaticMap() {
    if (!this.hasEmbedTarget) return;

    const container = this.embedTarget;
    container.innerHTML = "";

    // Static map image
    const img = document.createElement("img");
    img.src = this.staticUrlValue;
    img.alt = "Map showing church locations";
    img.className = "static-map-image";
    container.appendChild(img);

    // Overlay with CTA
    const overlay = document.createElement("div");
    overlay.className = "static-map-overlay";
    overlay.innerHTML = `
      <div class="static-map-cta">
        <span class="btn btn-sm btn-light shadow-sm">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            <line x1="11" y1="8" x2="11" y2="14"/>
            <line x1="8" y1="11" x2="14" y2="11"/>
          </svg>
          Explore Map
        </span>
      </div>
    `;
    container.appendChild(overlay);

    this.addUpgradeListeners();
  }

  addUpgradeListeners() {
    // Upgrade on click only
    const upgradeOnce = () => {
      this.embedTarget.removeEventListener("click", upgradeOnce);
      this.upgrade();
    };

    this.embedTarget.addEventListener("click", upgradeOnce);
  }

  async upgrade() {
    this.modeValue = "interactive";
    this.saveInteractivePreference(); // Remember for future page loads

    if (!this.hasEmbedTarget) return;
    this.embedTarget.innerHTML = ""; // Clear static content

    // Show loading state
    this.embedTarget.innerHTML = `
      <div class="map-loading">
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading map...</span>
        </div>
      </div>
    `;

    // Tell map_controller to initialize interactive map
    if (this.hasMapOutlet) {
      await this.mapOutlet.initializeInteractiveMap();
      this.mapOutlet.signalUpgraded();
    }
  }
}
